import React, { useState, useEffect, useRef } from "react";
import { MapPin, Clock } from "lucide-react";
import { getLocationSuggestions } from "../data/locationData";

const RECENT_KEY = "ducktail_recent_locations";

// ─── Highlighted Text ────────────────────────────────────────────────────────
// Wraps matching portion of text in a blue bold span (safe, uses split not innerHTML)
function HighlightText({ text, query }) {
  if (!query.trim()) return <span>{text}</span>;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "gi"));
  const lower = query.toLowerCase();
  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === lower ? (
          <strong key={i} className="text-blue-600 font-semibold">
            {part}
          </strong>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
}

// ─── LocationAutocomplete ─────────────────────────────────────────────────────
/**
 * Reusable location autocomplete input with:
 * - Recent searches (persisted in localStorage)
 * - Live city + locality suggestions
 * - Highlighted matching text
 * - Keyboard navigation (↑ ↓ Enter Escape)
 *
 * Props:
 *   value             string   — controlled input value
 *   onChange          fn       — called with raw string as user types
 *   onSelect          fn       — called with suggestion object on selection
 *   placeholder       string
 *   mode              'all' | 'city' | 'locality'
 *   cityFilter        string   — if set, only show localities of this city
 *   className         string   — outer wrapper (e.g. "flex-1")
 *   inputWrapperClassName string — visual container around input (for hero search)
 *   inputClassName    string   — applied to <input> directly
 *   renderPrefix      ReactNode— optional icon/element before the input
 */
const LocationAutocomplete = ({
  value = "",
  onChange,
  onSelect,
  placeholder = "Search city or locality...",
  mode = "all",
  cityFilter = null,
  className = "",
  inputWrapperClassName = "",
  inputClassName = "",
  renderPrefix = null,
  onDropdownToggle = () => { },
}) => {
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);

  // Sync isOpen with parent
  useEffect(() => {
    onDropdownToggle(isOpen);
  }, [isOpen, onDropdownToggle]);

  // ── Load recents from localStorage ─────────────────────────────────────────
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_KEY);
      if (stored) setRecentSearches(JSON.parse(stored));
    } catch { }
  }, []);

  // ── Compute suggestions on value/mode/cityFilter change ────────────────────
  useEffect(() => {
    setSuggestions(getLocationSuggestions(value, mode, cityFilter));
    setActiveIndex(-1);
  }, [value, mode, cityFilter]);

  // ── Close on click outside ──────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Keep active item scrolled into view ────────────────────────────────────
  useEffect(() => {
    if (listRef.current && activeIndex >= 0) {
      const items = listRef.current.querySelectorAll("[data-suggestion]");
      if (items[activeIndex]) {
        items[activeIndex].scrollIntoView({ block: "nearest" });
      }
    }
  }, [activeIndex]);

  // ── Save to recents ─────────────────────────────────────────────────────────
  const saveRecent = (item) => {
    try {
      const updated = [
        item,
        ...recentSearches.filter((r) => r.label !== item.label),
      ].slice(0, 5);
      setRecentSearches(updated);
      localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
    } catch { }
  };

  const handleSelect = (item) => {
    saveRecent(item);
    onSelect(item);
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const removeRecent = (label, e) => {
    e.stopPropagation();
    try {
      const updated = recentSearches.filter((r) => r.label !== label);
      setRecentSearches(updated);
      localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
    } catch { }
  };

  // ── Keyboard navigation ─────────────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (!isOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && suggestions[activeIndex]) {
        e.preventDefault();
        handleSelect(suggestions[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  // ── Visibility helpers ──────────────────────────────────────────────────────
  const trimmed = value.trim();
  const showRecent = isOpen && !trimmed && recentSearches.length > 0;
  const showSuggestions = isOpen && trimmed.length > 0 && suggestions.length > 0;
  const showNoResults = isOpen && trimmed.length > 0 && suggestions.length === 0;
  const showDropdown = showRecent || showSuggestions || showNoResults;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* ── Input wrapper (optional visual container for hero search) ─────── */}
      <div className={inputWrapperClassName}>
        {renderPrefix}
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={inputClassName}
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {/* ── Dropdown ──────────────────────────────────────────────────────── */}
      {showDropdown && (
        <div
          className="absolute top-full left-0 right-0 md:w-[600px] mt-0 bg-white rounded-none border-x border-b border-gray-100 z-[9999] overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.12)]"
        >
          <div ref={listRef} className="max-h-[520px] overflow-y-auto location-dropdown-list">

            {/* Recent Searches ──────────────────────────────────────────── */}
            {showRecent && (
              <div className="bg-gray-50/30">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Recent Searches</span>
                </div>
                <div className="p-3 flex flex-wrap gap-2">
                  {recentSearches.map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => handleSelect(item)}
                      className="group flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-none text-[12px] text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-all duration-150"
                    >
                      <Clock className="w-3 h-3 text-gray-400" />
                      <span>{item.label}</span>
                      <span
                        role="button"
                        tabIndex={-1}
                        onClick={(e) => removeRecent(item.label, e)}
                        className="opacity-0 group-hover:opacity-100 ml-1 text-gray-400 hover:text-red-500 transition-all"
                      >
                        ×
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Location Suggestions ─────────────────────────────────────── */}
            {showSuggestions && (
              <div className="divide-y divide-gray-50">
                {suggestions.map((item, i) => (
                  <button
                    key={i}
                    data-suggestion
                    type="button"
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setActiveIndex(i)}
                    className={`w-full flex items-center justify-between gap-4 px-6 py-3.5 text-left transition-all duration-75 ${activeIndex === i ? "bg-blue-50/50" : "bg-white hover:bg-gray-50/50"
                      }`}
                  >
                    <div className="flex flex-col min-w-0">
                      <span className="text-[14px] font-medium text-gray-800 leading-none mb-1.5">
                        <HighlightText text={item.display} query={value} />
                      </span>
                      {item.subtitle && (
                        <span className="text-[12px] text-gray-400 truncate">
                          {item.subtitle}
                        </span>
                      )}
                    </div>
                    <span className="text-[11px] font-semibold text-gray-300 uppercase tracking-widest flex-shrink-0">
                      {item.type === "city" ? "City" : item.type === "locality" ? "Locality" : item.type}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* No Results ───────────────────────────────────────────────── */}
            {showNoResults && (
              <div className="px-6 py-10 text-center bg-white">
                <p className="text-sm text-gray-400">
                  No matches found for <span className="font-semibold text-gray-500 underline underline-offset-4 decoration-blue-100">"{value}"</span>
                </p>
                <p className="text-[11px] text-gray-300 mt-2 uppercase tracking-tight">Try searching for a city or locality</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;
